import { getUserTypeId } from "../../../utils";
import Container from "react-bootstrap/Container";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import { Col, Row } from "react-bootstrap";
import * as Yup from "yup";
import LabelInsideInput from "../../../components/molecules/InputController/LabelInsideInputField";
import { useFormik } from "formik";
import React, { useContext, useEffect, useCallback, useState } from "react";
import { AppDispatch, RootState } from "../../../slices/store";
import { useDispatch, useSelector } from "react-redux";
import { isEqual, set } from "lodash";
import { TodosContext } from "../../../context";
import UseAuthConfig from "../../../hooks/useAuthConfig";
import { handleEditUser } from "../service/userProfileService";
import { useLocation, useNavigate } from "react-router-dom";
import useRequestSettings from "../../../hooks/useRequestClient";
import { clientsGet } from "../../../slices/clients/clientManagementSlice";
import Loader from "../../../components/atoms/Loader/Loader";
import { getSingleUser } from "../../../slices/users/userManagementSlice";
import { useMsal } from "@azure/msal-react";
import ActionModal from "../../../components/molecules/modal/ActionModal"; // Updated path

export default function EditUserProfile() {
  const { addAlert } = useContext(TodosContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { instance } = useMsal();
  const { protectedResources }: any = UseAuthConfig();
  const navigate = useNavigate();
  const [reqRequests] = useRequestSettings();
  const [isChanged, setIsChanged] = React.useState(false);
  const endpoint = protectedResources.apiTodoList.endpoint;
  const [isConfirmationModalShow, setIsConfirmationModalShow] =
    React.useState(false);
  const handleOpenModal = () => setIsConfirmationModalShow(true);
  const handleCloseModal = () => setIsConfirmationModalShow(false);
  const location = useLocation();
  const selectedUser = useSelector(
    (state: RootState) => state.userManagement.selectedUser
  );

  /** -----------------------------
   * Build original values object
   * ----------------------------- */
  const getOriginalValues = useCallback(() => {
    if (!selectedUser) return null;

    return {
      firstName: selectedUser.firstName ?? "",
      lastName: selectedUser.lastName ?? "",
      email: selectedUser.email ?? "",
      mobileNumber: selectedUser.mobileNumber ?? "",
      state: selectedUser.state
        ? { value: selectedUser.state, label: selectedUser.state }
        : null,
      jobTitle: selectedUser.jobTitle ?? "",
      companyName: selectedUser.companyName ?? "",
      type: selectedUser.type ?? "",
    };
  }, [selectedUser]);

  const formik = useFormik({
    initialValues: getOriginalValues() || {
      firstName: "",
      lastName: "",
      email: "",
      mobileNumber: "",
      state: null,
      jobTitle: "",
      companyName: "",
      type: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      email: Yup.string().required("Please enter email"),
      mobileNumber: Yup.string().required("Please enter mobile number"),
      state: Yup.object().nullable().required("Please select a state"),
      type: Yup.string().required("Please select a type"),
    }),
    onSubmit: async (values) => {
      if (!selectedUser) return;

      // Compare original vs current
      if (isEqual(values, getOriginalValues())) {
        addAlert("No changes detected", "warning", true);
        return;
      }
      setIsLoading(true);
      const stateValue =
        typeof values?.state?.value === "number"
          ? values.state.value
          : values?.state?.value === "Active"
          ? 2
          : 3;

      const payload = {
        ...selectedUser,
        ...values,
        state: stateValue,
        type: getUserTypeId(selectedUser.type),
      };

      const url = `${endpoint}/users/update-current-user`;
      try {
        // Await the edit call
        await handleEditUser(
          payload,
          dispatch,
          setIsLoading,
          addAlert,
          false, // don't auto-redirect
          navigate,
          protectedResources,
          url,
          reqRequests,
          clientsGet
        );
        // Then show modal after alert
        setTimeout(() => {
          handleOpenModal();
        }, 1000); // small delay to let alert finish
      } catch (e) {
        // handle error already alerted in handleEditUser
        handleCloseModal();
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleLogout = () => {
    // Clear tokens and redirect to login
    localStorage.clear();
    instance.logoutRedirect();
  };

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    if (pathParts.length === 4) {
      const id = pathParts[3];
      const accessToken = localStorage.getItem("AccessToken") || "";
      const url = `${endpoint}/users/current-user`;
      dispatch(getSingleUser({ accessToken, url }));
    }
  }, [location.pathname, dispatch, endpoint]);

  /** -----------------------------
   * Detect if form is changed
   * ----------------------------- */
  useEffect(() => {
    if (!selectedUser) return;

    const original = getOriginalValues();
    const changed = !isEqual(formik.values, original);
    setIsChanged(changed);
  }, [formik.values, selectedUser, getOriginalValues]);

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline
        name="Edit User"
        subHeading="Update user details and account information"
      />

      <div className="d-flex">
        <form onSubmit={formik.handleSubmit}>
          {/* First row */}
          <div className="box-container">
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="firstName"
                  label="First Name"
                  {...formik.getFieldProps("firstName")}
                  error={formik.errors.firstName}
                  touched={formik.touched.firstName}
                  showLabel={false}
                />
              </Col>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="lastName"
                  label="Last Name"
                  {...formik.getFieldProps("lastName")}
                  error={formik.errors.lastName}
                  touched={formik.touched.lastName}
                  showLabel={false}
                />
              </Col>
            </Row>

            {/* Second row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="email"
                  label="Email"
                  {...formik.getFieldProps("email")}
                  error={formik.errors.email}
                  touched={formik.touched.email}
                  showLabel={false}
                  disabled={true}
                />
              </Col>

              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="mobileNumber"
                  label="Mobile Number"
                  {...formik.getFieldProps("mobileNumber")}
                  error={formik.errors.mobileNumber}
                  touched={formik.touched.mobileNumber}
                  showLabel={false}
                />
              </Col>
            </Row>

            {/* Third row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="jobTitle"
                  label="Job Title"
                  {...formik.getFieldProps("jobTitle")}
                  error={formik.errors.jobTitle}
                  touched={formik.touched.jobTitle}
                  showLabel={false}
                />
              </Col>

              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="type"
                  label="Type"
                  {...formik.getFieldProps("type")}
                  error={formik.errors.type}
                  touched={formik.touched.type}
                  showLabel={false}
                  disabled={true}
                />
              </Col>
            </Row>

            {/* Fourth row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="companyName"
                  label="Company Name"
                  {...formik.getFieldProps("companyName")}
                  error={formik.errors.companyName}
                  touched={formik.touched.companyName}
                  showLabel={false}
                />
              </Col>
            </Row>
          </div>

          <div className="footer-btn-section box-container d-flex justify-content-end gap-3">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            <button
              className="btn btn-primary"
              type="submit"
              disabled={!isChanged || isLoading}
              style={{
                opacity: !isChanged ? 0.6 : 1,
                cursor: !isChanged ? "not-allowed" : "pointer",
              }}
            >
              Submit
            </button>
          </div>
        </form>

        {isLoading ? <Loader /> : null}
      </div>

      <ActionModal
        title={"Update Successful"}
        show={isConfirmationModalShow}
        onClose={handleCloseModal}
        onConfirm={handleLogout}
      >
        <p>Now logout and login your account.</p>
      </ActionModal>
    </Container>
  );
}
